import { Users } from '@platform/client';
import { Button, Dropdown, DropdownItem, Icon } from '@platform/components';
import { Separator } from 'react-aria-components';
import { Link } from 'wouter';
import dataTestids from '../constants/test-ids';
import LeftNavPanel from '../dashboard/LeftNavPanel';
import OrganizationDropdown from '../dashboard/OrganizationDropdown';
import ProfileDropdown from '../dashboard/ProfileDropdown';
import { ROUTES } from '../routes/consts';
import FlexibleLink from '../util/FlexibleLink';
import getDashboardSubHeaderText from './getDashboardSubHeaderText';
import { MovingWorldsBannerLogo } from './MovingWorldsLogo';
import cx from 'classix';

const SegmentedDashboardNavButton = () => {
  const { data: user } = Users.useGetCurrentUser();
  let dashboardUrl = `platform${ROUTES.DASHBOARD}`;
  if (user?.role === 'admin') {
    dashboardUrl = `platform${ROUTES.ADMIN}`;
  }
  return (
    <div className='flex'>
      <Button variant='accent' size='lg' additionalClassnames='rounded-r-none' href={dashboardUrl}>
        {`${user?.firstName}'s Dashboard`}
      </Button>
      <Dropdown
        type='button'
        ariaLabel='test'
        buttonProps={{
          size: 'lg',
          variant: 'accent',
          children: <span></span>,
          additionalClassnames: 'rounded-l-none !shadow-none !border-none',
        }}
      >
        <DropdownItem href={dashboardUrl}>Go to Dashboard</DropdownItem>
        <Separator />
        <DropdownItem href={import.meta.env.VITE_API_HOST + '/login/logout'}>Logout</DropdownItem>
      </Dropdown>
    </div>
  );
};

/** The blue bar at the top of every page containing the MovingWorlds logo */
const AuthedNavbar = ({
  isSidebarOpen,
  setIsSidebarOpen,
  sidebarRef,
  showDashboardDropdown,
}: {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
  sidebarRef: React.RefObject<HTMLDivElement>;
  /** Whether to show the "Username's Dashboard" segmented dropdown for dashboard & logout navigation */
  showDashboardDropdown?: boolean;
}) => {
  const { data: user } = Users.useGetCurrentUser();

  const { navText, cohortUrl, cohortDisplayName } = getDashboardSubHeaderText(user);

  return (
    <div
      className={cx(
        'z-40 sticky top-0 md:relative md:top-auto bg-brand-500 h-[54px]',
        'md:h-[var(--navbar-height)] px-4 lg:px-10 py-6 flex items-center',
      )}
      data-testid={dataTestids.LAYOUT.AUTHED_NAVBAR}
    >
      <div className='text-white'>
        {' '}
        {/* Lazy fix for bad SVG sizing; need to re-export the SVG */}
        <div data-testid={dataTestids.LAYOUT.HEADER.LOGO}>
          <Link to={'~' + ROUTES.HOME}>
            {/* The legacy app uses a different size around roughly the 'md' breakpoint here, but this has been ignored in favor of us redefining how we want to handle mobile responsiveness in the future */}
            <div className='w-[200px] lg:w-[272px]'>
              <MovingWorldsBannerLogo />
            </div>
          </Link>
        </div>
      </div>
      {/* Optional button for navigating into the user's dashboard or logging out */}
      {showDashboardDropdown && (
        <div className='hidden sm:block ml-auto'>
          <SegmentedDashboardNavButton />
        </div>
      )}
      {/* This is unique segmented-button/dropdown for this navbar. Long-term we should extract this into the
       * design system */}
      {/* Mobile nav lide panel button */}
      <button
        className='md:hidden ml-auto cursor-pointer mr-[-12px] p-4'
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        data-testid={dataTestids.LAYOUT.HEADER.MOBILE_SIDEPANEL_BUTTON}
      >
        <Icon type='menu-02' size='24' stroke='white' />
      </button>

      {isSidebarOpen && (
        <div
          className='md:hidden fixed top-0 left-0 w-full h-screen bg-black bg-opacity-50 z-50'
          data-testid={dataTestids.LAYOUT.HEADER.MOBILE_SIDEPANEL}
        >
          <div
            ref={sidebarRef}
            className='fixed top-0 left-0 overflow-y-auto w-[calc(100vw-64px)] h-screen bg-white z-50'
          >
            <div className='flex flex-col h-fit py-2 gap-2'>
              {(!!navText?.length || !!cohortDisplayName?.length) && (
                <div className='ml-2 mt-2 flex flex-col gap-1'>
                  {!!navText?.length && (
                    <div className='text-lg font-semibold text-gray-700'>{navText}</div>
                  )}
                  {!!cohortDisplayName?.length && (
                    <div className='text-sm font-semibold text-gray-400'>
                      {cohortUrl ?
                        <FlexibleLink
                          className='font-bold underline-offset-8 decoration-2 hover:underline'
                          href={cohortUrl}
                          type='external'
                        >
                          {cohortDisplayName} Cohort
                        </FlexibleLink>
                      : `${cohortDisplayName} Cohort`}
                    </div>
                  )}
                </div>
              )}
              <div>
                <ProfileDropdown />
                {user?.accountType === 'organization' && user.organization && (
                  <OrganizationDropdown />
                )}
                <hr />
                <LeftNavPanel />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthedNavbar;
